import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    mdiPassportBiometric,
    mdiPlusCircleOutline,
    mdiPencil,
    mdiBriefcaseAccountOutline,
    mdiCash,
    mdiBank,
    mdiCalendar
} from '@mdi/js';
import { useRouteMatch, Link } from 'react-router-dom';
import {
    Button,
    Form,
    Input,
    Row,
    Col,
    InputGroup,
    InputGroupAddon
} from 'reactstrap';
import Icon from '@mdi/react';
import { loadDevices } from '../../../redux/modules/devices';
import StyledTable from '../../../components/StyledTable/StyledTable';
import FullPageLoader from '../../../components/FullPageLoader/FullPageLoader';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { Device } from '../../../models';
import moment from 'moment';
import '../../../components/FiltersForm/FiltersForm.css';

const DevicesList = () => {
    const dispatch                          = useDispatch();
    const { path }                          = useRouteMatch();
    const { t }                             = useTranslation();
    const { isLoading, devices }            = useSelector(state => state.devices);
    const { accounts = [] }                 = useSelector(state => state.accounts || {});
    const { payment_systems = [] }          = useSelector(state => state.payment_systems || {});
    const user                              = useSelector(state => state.auth.user);
    const defaultFilters                    = useMemo(() => ({
        account_id: user && !user.isSuperAdmin() ? user.account_id : '',
        type: '',
        payment_system_id: '',
        date: ''
    }), [user]);
    const [filters, setFilters]             = useState(defaultFilters);

    const getAppliedFilters = (rawFilters) => {
        const safeFilters = rawFilters || {};
        return Object.keys(safeFilters).reduce((memo, key) => {
            const value = safeFilters[key];
            if (value !== '' && value !== null && typeof value !== 'undefined') {
                memo[key] = value;
            }
            return memo;
        }, {});
    };

    useEffect(() => {
        const nextFilters = {
            account_id: user && !user.isSuperAdmin() ? user.account_id : '',
            type: '',
            payment_system_id: '',
            date: ''
        };
        setFilters(nextFilters);
        dispatch(loadDevices(getAppliedFilters(nextFilters)));
    }, [dispatch, user]);

    if (!devices || isLoading) {
        return <FullPageLoader />
    }

    const availableAccounts = user && !user.isSuperAdmin()
        ? accounts.filter(account => account.id === user.account_id)
        : accounts;

    const applyFilters = (event) => {
        event.preventDefault();
        dispatch(loadDevices(getAppliedFilters(filters)));
    };

    const cellFormatter = (text, smallText, capitalizeSmallText) =>
        <>
            <div>
                { text }
            </div>
            {smallText &&
                <small className={capitalizeSmallText ? 'capitalize': ''}>{ smallText }</small>
            }
        </>

    const actionFormatter = (id, device) =>
        <Button tag={ Link } to={ `${path}/edit/${id}` } color="success"><Icon path={ mdiPencil } size={ 1 } color="white" /></Button>;

    const columns = [{
        dataField: 'name',
        text: t('devices.name'),
        sort: true,
        formatter: (name, device) => cellFormatter(
            name,
            device.identifier
        )
    }, {
        dataField: 'account.name',
        text: t('devices.account'),
        sort: true
    }, {
        dataField: 'identifier',
        text: t('devices.identifier'),
        sort: true,
        hidden: true
    }, {
        dataField: 'type',
        text: t('devices.type'),
        sort: true
    }, {
        dataField: 'payment_system.name',
        text: t('devices.payment_system'),
        sort: true
    }, {
        dataField: 'start_at',
        text: t('devices.start_at'),
        sort: true,
        formatter: (start_at, row) => cellFormatter(
            moment(start_at).format('lll')
        ),
        csvFormatter: (cell, row, rowIndex, formatExtraData) => {
            return moment(cell).format('YYYY-MM-DD HH:mm');
        }
    }, {
        dataField: 'stop_at',
        text: t('devices.stop_at'),
        sort: true,
        formatter: (stop_at, row) => cellFormatter(
            moment(stop_at).format('lll')
        ),
        csvFormatter: (cell, row, rowIndex, formatExtraData) => {
            return moment(cell).format('YYYY-MM-DD HH:mm');
        }
    }, {
        dataField: 'id',
        text: t('common.actions'),
        sort: false,
        formatter: actionFormatter,
        csvExport: false
    }];

    const defaultSorted = [{
        dataField: 'name',
        order: 'desc'
    }];

    const actions = <Link to={`${path}/new`}>
        <Button color="info">
            <Icon path={ mdiPlusCircleOutline } size={ 1 } color="white" /> { t('devices.add') }
        </Button>
    </Link>;

    return (
        <div className="DevicesList">
            <PageTitle
                title={ t('devices.list_title') }
                icon={ <Icon path={ mdiPassportBiometric } color="white" /> }
                actions={ actions }
            />
            <Form onSubmit={ applyFilters } className="FiltersForm noPrint">
                <Row>
                    <Col sm={6} md={3}>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiBriefcaseAccountOutline } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                id="device-filter-account"
                                type="select"
                                className="custom-select"
                                value={ filters.account_id }
                                onChange={ (event) => setFilters({ ...filters, account_id: event.target.value }) }
                                disabled={ user && !user.isSuperAdmin() }
                            >
                                <option value="">{ t('devices.filters.all_accounts') }</option>
                                {availableAccounts.map(account => (
                                    <option key={ account.id } value={ account.id }>{ account.name }</option>
                                ))}
                            </Input>
                        </InputGroup>
                    </Col>
                    <Col sm={6} md={3}>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiCash } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                id="device-filter-type"
                                type="select"
                                className="custom-select"
                                value={ filters.type }
                                onChange={ (event) => setFilters({ ...filters, type: event.target.value }) }
                            >
                                <option value="">{ t('devices.filters.all_types') }</option>
                                {Device.types().map(type => (
                                    <option key={ type } value={ type }>{ t(`devices.types.${type}`) }</option>
                                ))}
                            </Input>
                        </InputGroup>
                    </Col>
                    <Col sm={6} md={3}>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiBank } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                id="device-filter-provider"
                                type="select"
                                className="custom-select"
                                value={ filters.payment_system_id }
                                onChange={ (event) => setFilters({ ...filters, payment_system_id: event.target.value }) }
                            >
                                <option value="">{ t('devices.filters.all_providers') }</option>
                                {payment_systems.map(paymentSystem => (
                                    <option key={ paymentSystem.id } value={ paymentSystem.id }>{ paymentSystem.name }</option>
                                ))}
                            </Input>
                        </InputGroup>
                    </Col>
                    <Col sm={6} md={3}>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiCalendar } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                id="device-filter-date"
                                type="date"
                                className="custom-select"
                                value={ filters.date }
                                onChange={ (event) => setFilters({ ...filters, date: event.target.value }) }
                            />
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="btn-row">
                    <Col sm="12" md={{ size: 4, offset: 4 }}>
                        <Button type="submit" block color="info">{ t('common.validate') }</Button>
                    </Col>
                </Row>
            </Form>
            <StyledTable
                bootstrap4
                keyField="id"
                data={ devices }
                columns={ columns }
                defaultSorted={ defaultSorted }
                sizePerPage= { 25 }
            />
        </div>
    );
}

export default DevicesList;
